import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Circle, Truck, Scale, PackageCheck, XCircle, Lock, MapPin, Pencil, Eye, Trash2 } from 'lucide-react';
import { getConferimento, eliminaConferimentoStoccato } from '../api/conferimenti';
import type { ConferimentoDetail } from '../api/conferimenti';
import { apriFirmatoPdfOmologa } from '../api/omologhe';
import ArrivoModal from '../components/modals/ArrivoModal';
import PesataModal from '../components/modals/PesataModal';
import StoccaggioModal from '../components/modals/StoccaggioModal';
import RespingiConferimentoModal from '../components/modals/RespingiConferimentoModal';
import EditConferimentoModal from '../components/modals/EditConferimentoModal';
import EliminaMagazzinoModal from '../components/magazzino/EliminaMagazzinoModal';
import { useAuthStore } from '../store/authStore';
import { fmtKg } from '../lib/saldo';
import { canTransizioneConferimento, canEditConferimento, canEliminaConferimento } from '../lib/constants';

// Stati conferimento #91 (no in_lavorazione/lavorazione_completata: lavorazione = Blocco 8).
const STATUS_CLS: Record<string, string> = {
  programmato: 'bg-blue-100 text-blue-700',
  in_ritardo: 'bg-red-100 text-red-700',
  in_transito: 'bg-cyan-100 text-cyan-700',
  attesa_compliance: 'bg-amber-100 text-amber-700',
  in_stoccaggio: 'bg-green-100 text-green-700',
  respinto: 'bg-purple-100 text-purple-700',
};

function formatDT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}
function kg(v: number | null): string {
  return v != null ? `${fmtKg(v)} kg` : '—';
}

export default function DettaglioConferimentoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const [conf, setConf] = useState<ConferimentoDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<'arrivo' | 'pesata' | 'stoccaggio' | 'respingi' | 'edit' | 'elimina' | null>(null);

  // Due capacità DIVERSE, non allineabili: avanzare lo stato ≠ correggere
  // l'anagrafica. Il perché di DIR-sì/RT-no e OP-sì/OP-no sta sulle costanti,
  // in lib/constants.ts. Gate cosmetici: l'autorità resta il backend.
  const canTransition = canTransizioneConferimento(user?.role);
  const canEditConf = canEditConferimento(user?.role);
  // Gate COSMETICO: l'autorità è il require_role("admin") del backend. Le altre
  // condizioni replicano ALCUNE guardie del 422/409 per non offrire un bottone
  // che risponderebbe errore — non tutte, e non per sostituirle: l'«ultimo
  // conferimento vivo dell'ordine» resta affidato al 409, perché qui i
  // fratelli non si conoscono.
  const canElimina = canEliminaConferimento(user?.role);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        setConf(await getConferimento(id));
      } catch {
        setError(t('conferimenti.dettaglio.non_trovato'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  function handleUpdated(updated: ConferimentoDetail) {
    setConf(updated);
    setModal(null);
  }

  if (loading) return <div className="p-8 text-center text-ink-soft text-sm">{t('conferimenti.dettaglio.caricamento')}</div>;
  if (error || !conf) return (
    <div className="p-8">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink-dark mb-4"><ArrowLeft size={14} /> {t('conferimenti.dettaglio.indietro')}</button>
      <p className="text-sm text-accent-danger">{error ?? t('conferimenti.dettaglio.errore')}</p>
    </div>
  );

  const statusKey = conf.status_display ?? conf.status;
  const statusCls = STATUS_CLS[statusKey] ?? STATUS_CLS[conf.status] ?? 'bg-stone-100 text-ink-mid';
  const statusLabel = t(`conferimenti.card.stato.${statusKey}`, { defaultValue: conf.status });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink-dark mb-4">
        <ArrowLeft size={14} /> {t('conferimenti.dettaglio.back_programmazione')}
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-ink-dark font-mono">{conf.numero ?? '—'}</h1>
          {conf.order && (
            <p className="text-sm text-ink-soft mt-0.5">
              {t('conferimenti.dettaglio.ordine_label')}{' '}
              <button type="button" onClick={() => navigate(`/richieste/${conf.order!.id}`)}
                data-testid="order-link"
                className="font-medium text-sage-dark hover:underline">
                {conf.order.codice_ordine}
              </button> — {conf.order.customer_ragione_sociale}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {(() => {
            const firmata = conf.omologa?.stato_firma === 'firmata' && !!conf.omologa?.pdf_firmato_url;
            return (
              <button type="button" disabled={!firmata}
                onClick={() => { if (conf.omologa) void apriFirmatoPdfOmologa(conf.omologa.id); }}
                data-testid="btn-anteprima-omologa"
                title={firmata ? undefined : t('conferimenti.dettaglio.omologa_non_firmata')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                <Eye size={12} /> {t('conferimenti.dettaglio.anteprima_omologa')}
              </button>
            );
          })()}
          {canEditConf && !conf.fattura_id && (
            <button type="button" onClick={() => setModal('edit')} data-testid="btn-modifica-conferimento"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors">
              <Pencil size={12} /> {t('conferimenti.dettaglio.modifica')}
            </button>
          )}
          {canElimina && conf.status === 'in_stoccaggio' && !conf.fattura_id && !conf.fattura_passiva_id && (
            <button type="button" onClick={() => setModal('elimina')} data-testid="btn-elimina-conferimento"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg transition-colors">
              <Trash2 size={12} /> {t('conferimenti.dettaglio.elimina')}
            </button>
          )}
          <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${statusCls}`}>{statusLabel}</span>
        </div>
      </div>

      {/* #2: conferimento agganciato a una fattura (bozza/emessa) → non modificabile.
          Avviso cosmetico; l'autorità è il 422 del backend su PATCH.

          DUE MESSAGGI, non uno con un numero vuoto: una BOZZA non ha
          `numero_fattura` (è NULL fino all'emissione), e «Conferimento in
          fattura (—)» mandava a cercare fra le EMESSE una fattura che non
          esiste ancora — la bozza si scarta dal workspace. Il link porta
          dritto al posto giusto nei due casi. */}
      {conf.fattura_id && (
        <div data-testid="conf-bloccato-fattura" className="mb-4 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <Lock size={13} className="mt-0.5 flex-shrink-0" />
          {conf.stato_fattura === 'bozza' ? (
            <span>
              {t('conferimenti.dettaglio.bloccato_bozza')}{' '}
              <Link to={`/fatturazione?bozza=${conf.fattura_id}`} data-testid="link-bozza-bloccante"
                className="font-semibold underline hover:no-underline">
                {t('conferimenti.dettaglio.bloccato_vai_bozza')}
              </Link>
            </span>
          ) : (
            <span>
              {t('conferimenti.dettaglio.bloccato_fattura', { numero: conf.numero_fattura ?? '—' })}{' '}
              <Link to={`/fatturazione?fattura=${conf.fattura_id}`} data-testid="link-fattura-bloccante"
                className="font-semibold underline hover:no-underline">
                {t('conferimenti.dettaglio.bloccato_vai_fattura')}
              </Link>
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {/* Sito di produzione (origine materiale) — sola lettura */}
        {conf.order && (
          <div className="bg-white border border-stone-mid rounded-xl p-4">
            <h2 className="text-sm font-semibold text-ink-dark mb-3 flex items-center gap-2"><MapPin size={14} className="text-ink-soft" /> {t('conferimenti.dettaglio.sez_sito')}</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.sito_indirizzo')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.order.origine_indirizzo ?? '—'}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.sito_comune')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.order.origine_comune ?? '—'}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.sito_provincia')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.order.origine_provincia ?? '—'}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.sito_cap')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.order.origine_cap ?? '—'}</p></div>
            </div>
          </div>
        )}

        {/* Programmazione */}
        <div className="bg-white border border-stone-mid rounded-xl p-4">
          <h2 className="text-sm font-semibold text-ink-dark mb-3 flex items-center gap-2"><Circle size={14} className="text-ink-soft" /> {t('conferimenti.dettaglio.sez_programmazione')}</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.data_prevista')}</span><p className="text-ink-dark font-medium mt-0.5">{formatDate(conf.data_prevista)}</p></div>
            <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.peso_stimato')}</span><p className="text-ink-dark font-medium mt-0.5">{kg(conf.peso_stimato_kg)}</p></div>
            <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.area_tentativa')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.area_tentativa?.codice ?? '—'}</p></div>
            {conf.note_programmazione && (
              <div className="col-span-2"><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.note')}</span><p className="text-ink-dark mt-0.5">{conf.note_programmazione}</p></div>
            )}
          </div>
        </div>

        {/* Arrivo / in transito */}
        <div className="bg-white border border-stone-mid rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-ink-dark flex items-center gap-2">
              <Truck size={14} className={conf.in_transito_at ? 'text-sage' : 'text-ink-faint'} /> {t('conferimenti.dettaglio.sez_arrivo')}
            </h2>
            {conf.status === 'programmato' && canTransition && (
              <button type="button" onClick={() => setModal('arrivo')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors">
                <Truck size={12} /> {t('conferimenti.dettaglio.registra_arrivo')}
              </button>
            )}
          </div>
          {conf.in_transito_at ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.data_arrivo')}</span><p className="text-ink-dark font-medium mt-0.5">{formatDT(conf.in_transito_at)}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.numero_fir')}</span><p className="text-ink-dark font-medium mt-0.5 font-mono">{conf.numero_fir ?? '—'}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.peso_lordo')}</span><p className="text-ink-dark font-medium mt-0.5">{kg(conf.peso_lordo_kg)}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.radiometrico')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.controllo_radiometrico_ok == null ? '—' : conf.controllo_radiometrico_ok ? t('conferimenti.dettaglio.radio_ok') : t('conferimenti.dettaglio.radio_ko')}</p></div>
            </div>
          ) : (
            <p className="text-sm text-ink-faint italic">{t('conferimenti.dettaglio.non_registrato_m')}</p>
          )}
        </div>

        {/* Pesata / attesa compliance */}
        <div className="bg-white border border-stone-mid rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-ink-dark flex items-center gap-2">
              <Scale size={14} className={conf.attesa_compliance_at ? 'text-sage' : 'text-ink-faint'} /> {t('conferimenti.dettaglio.sez_pesata')}
            </h2>
            {conf.status === 'in_transito' && canTransition && (
              <button type="button" onClick={() => setModal('pesata')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors">
                <Scale size={12} /> {t('conferimenti.dettaglio.registra_pesata')}
              </button>
            )}
          </div>
          {conf.attesa_compliance_at ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.data_pesata')}</span><p className="text-ink-dark font-medium mt-0.5">{formatDT(conf.attesa_compliance_at)}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.tara')}</span><p className="text-ink-dark font-medium mt-0.5">{kg(conf.tara_kg)}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.peso_netto')}</span><p className="text-ink-dark font-medium mt-0.5">{kg(conf.peso_netto_kg)}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.origine_netto')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.peso_netto_origine === 'manuale_amm' ? t('conferimenti.dettaglio.origine_manuale') : conf.peso_netto_origine === 'pesata_calcolata' ? t('conferimenti.dettaglio.origine_calcolato') : '—'}</p></div>
            </div>
          ) : (
            <p className="text-sm text-ink-faint italic">{t('conferimenti.dettaglio.non_registrata_f')}</p>
          )}
        </div>

        {/* Stoccaggio */}
        <div className="bg-white border border-stone-mid rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-ink-dark flex items-center gap-2">
              <PackageCheck size={14} className={conf.compliance_confermata_at ? 'text-sage' : 'text-ink-faint'} /> {t('conferimenti.dettaglio.sez_stoccaggio')}
            </h2>
            {conf.status === 'attesa_compliance' && canTransition && (
              <button type="button" onClick={() => setModal('stoccaggio')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors">
                <PackageCheck size={12} /> {t('conferimenti.dettaglio.conferma_stoccaggio')}
              </button>
            )}
          </div>
          {conf.compliance_confermata_at ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.data_stoccaggio')}</span><p className="text-ink-dark font-medium mt-0.5">{formatDT(conf.compliance_confermata_at)}</p></div>
              <div><span className="text-ink-soft text-xs">{t('conferimenti.dettaglio.area_effettiva')}</span><p className="text-ink-dark font-medium mt-0.5">{conf.area_effettiva?.codice ?? '—'}</p></div>
            </div>
          ) : (
            <p className="text-sm text-ink-faint italic">{t('conferimenti.dettaglio.non_stoccaggio')}</p>
          )}
        </div>

        {/* Respingi (da programmato/in_transito) */}
        {['programmato', 'in_transito'].includes(conf.status) && canTransition && (
          <div className="flex justify-end">
            <button type="button" onClick={() => setModal('respingi')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg transition-colors">
              <XCircle size={12} /> {t('conferimenti.dettaglio.respingi')}
            </button>
          </div>
        )}
      </div>

      {modal === 'arrivo' && (
        <ArrivoModal conferimentoId={conf.id} numero={conf.numero} onClose={() => setModal(null)} onSaved={handleUpdated} />
      )}
      {modal === 'pesata' && (
        <PesataModal conferimentoId={conf.id} numero={conf.numero} pesoLordoKg={conf.peso_lordo_kg} onClose={() => setModal(null)} onSaved={handleUpdated} />
      )}
      {modal === 'stoccaggio' && (
        <StoccaggioModal conferimentoId={conf.id} numero={conf.numero} areaTentativaId={conf.area_tentativa_id} pesoNettoKg={conf.peso_netto_kg} cerCode={conf.order?.cer_code ?? null} cerAttivoId={conf.order?.cer_attivo_id ?? null} onClose={() => setModal(null)} onSaved={handleUpdated} />
      )}
      {modal === 'respingi' && (
        <RespingiConferimentoModal conferimentoId={conf.id} numero={conf.numero} onClose={() => setModal(null)} onSaved={handleUpdated} />
      )}
      {modal === 'edit' && (
        <EditConferimentoModal conferimento={conf} onClose={() => setModal(null)} onSaved={handleUpdated} />
      )}
      {/* L'avvertenza Ecomatico non è decorativa: il flag «registrato su
          Ecomatico» esiste su scarichi, produzioni e uscite ma NON sui
          conferimenti, quindi il backend non può bloccare nulla e la verifica
          resta a chi elimina. */}
      {modal === 'elimina' && (
        <EliminaMagazzinoModal
          testid="elimina-conferimento-modal"
          title={t('conferimenti.dettaglio.elimina_titolo')}
          righe={[
            t('conferimenti.dettaglio.elimina_riepilogo', {
              numero: conf.numero ?? conf.id,
              fir: conf.numero_fir ?? '—',
              kg: fmtKg(conf.peso_netto_kg ?? 0),
              area: conf.area_effettiva?.codice ?? '—',
            }),
            t('conferimenti.dettaglio.elimina_effetto'),
          ]}
          motivoLabel={t('conferimenti.dettaglio.elimina_motivo')}
          motivoPlaceholder={t('conferimenti.dettaglio.elimina_motivo_placeholder')}
          avvertenza={t('conferimenti.dettaglio.elimina_ecomatico')}
          onConfirm={async (motivo) => {
            await eliminaConferimentoStoccato(conf.id, motivo);
            // `/conferimenti` NON e' una rotta (il router ha solo
            // `conferimenti/:id`): l'eliminazione riusciva e l'utente finiva
            // sulla 404, cioe' leggeva «non trovato» dopo un'operazione andata
            // a buon fine. Si torna all'ORDINE, che e' il contesto da cui si
            // arriva e l'unico posto in cui l'esito si vede: la sua
            // ConferimentiCard non ha piu' quella riga. `/ordini/:id` e non
            // `/richieste/:id` perche' un conferimento esiste solo sotto un
            // ordine confermato, ed e' quel ramo a dare alla pagina la cornice
            // giusta (`isOrdineRoute`). Senza ordine in risposta si ripiega su
            // Programmazione, la lista da cui si entra qui.
            navigate(conf.order ? `/ordini/${conf.order.id}` : '/programmazione',
              { state: { toast: t('conferimenti.dettaglio.elimina_ok') } });
          }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
